import { Component, Inject, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '../../services/auth.service';
import { hashIdentifier } from '../../services/auth.service';

@Component({
  selector: 'app-otp-verification-modal',
  templateUrl: './otp-verification-modal.component.html',
  styleUrls: ['./otp-verification-modal.component.scss']
})
export class OtpVerificationModalComponent implements AfterViewInit {
  // QueryList to get references to all OTP input fields
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  otp: string[] = ['', '', '', '', '', '']; // Array to hold each digit of the OTP
  errorMessage: string = '';
  isLoading = false;
  trustDevice: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<OtpVerificationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { token: string, email: string, password: string }, // Data passed to the modal (token, email, and password)
    private authService: AuthService
  ) { }

  ngAfterViewInit(): void {
    // Focus the first OTP input field once the view is initialized
    if (this.otpInputs && this.otpInputs.first) {
      this.otpInputs.first.nativeElement.focus();
    }
  }

  /**
   * Handles input events for OTP fields.
   * Moves focus to the next input field automatically.
   * @param event The input event.
   * @param index The index of the current input field.
   */
  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    // Only allow numbers
    value = value.replace(/\D/g, '');
    if (value.length > 1) {
      // If pasting, handle 6-digit paste
      if (value.length === 6) {
        for (let i = 0; i < 6; i++) {
          this.otp[i] = value[i];
        }
        // Focus last input
        if (this.otpInputs && this.otpInputs.length) {
          this.otpInputs.toArray()[5].nativeElement.focus();
        }
        return;
      } else {
        value = value.charAt(0);
      }
      input.value = value;
    }
    this.otp[index] = value;
    // Move focus to the next input if a digit was entered and it's not the last field
    if (value && index < this.otpInputs.length - 1) {
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }
  }

  /**
   * Handles keydown events for OTP fields (e.g., Backspace to move to previous field).
   * @param event The keyboard event.
   * @param index The index of the current input field.
  //  */
  // onKeydown(event: KeyboardEvent, index: number): void {
  //   // Prevent non-numeric key presses
  //   if (event.key && !/\d/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Enter') {
  //     event.preventDefault();
  //     return;
  //   }
  //   if (event.key === 'Backspace' && this.otp[index] === '' && index > 0) {
  //     this.otpInputs.toArray()[index - 1].nativeElement.focus();
  //   } else if (event.key === 'Enter') {
  //     event.preventDefault();
  //     this.verifyOtp();
  //   }
  // }

  /**
   * Handles the paste event for OTP fields.
   * @param event The paste event.
   */
  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '');
    console.log(digits);
    if (digits.length === 6) {
      const inputArray = this.otpInputs.toArray();
  
      for (let i = 0; i < 6; i++) {
        this.otp[i] = digits[i];
        inputArray[i].nativeElement.value = digits[i];
      }
  
      // Focus the last field
      inputArray[5].nativeElement.focus();
  
      event.preventDefault();
    }
  }
  
  onOtpBackspace(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && this.otp[index] === '' && index > 0) {
      this.otpInputs.toArray()[index - 1].nativeElement.focus();
      event.preventDefault();
    }
  }

  /**
   * Initiates the OTP verification process.
   * Calls the AuthService to verify the concatenated OTP.
   */
  async verifyOtp(): Promise<void> {
    const fullOtp = this.otp.join(''); // Concatenate all digits
    if (fullOtp.length !== 6 || !/^\d{6}$/.test(fullOtp)) {
      this.errorMessage = 'Please enter a valid 6-digit code.';
      return;
    }

    this.errorMessage = ''; // Clear previous errors
    this.isLoading = true; // Show loading spinner

    try {
      let deviceToken;
      if (this.trustDevice && this.data.email) {
        const trustedDevices = JSON.parse(localStorage.getItem('trustedDeviceTokens') || '{}');
        const key = await hashIdentifier(this.data.email);
        if (trustedDevices[key] && Array.isArray(trustedDevices[key]) && trustedDevices[key].length > 0) {
          deviceToken = trustedDevices[key][0]; // Use first device token
        } else if (trustedDevices[key]) {
          // Backward compatibility for single token
          deviceToken = trustedDevices[key];
        }
      }
      const response = await this.authService.verifyOtp(this.data.token, fullOtp, this.trustDevice, this.data.email, this.data.password, deviceToken).then(r => r);

      if (response?.error) {
        this.errorMessage = response.error;
      } else if (response?.token) {
        if (this.trustDevice && response.device_token && this.data.email) {
          const trustedDevices = JSON.parse(localStorage.getItem('trustedDeviceTokens') || '{}');
          const key = await hashIdentifier(this.data.email);
          
          // Initialize as array if not exists
          if (!trustedDevices[key]) {
            trustedDevices[key] = [];
          } else if (!Array.isArray(trustedDevices[key])) {
            // Convert existing single token to array for backward compatibility
            trustedDevices[key] = [trustedDevices[key]];
          }
          
          // Add new device token if not already present
          if (!trustedDevices[key].includes(response.device_token)) {
            trustedDevices[key].push(response.device_token);
          }
          
          localStorage.setItem('trustedDeviceTokens', JSON.stringify(trustedDevices));
        }
        this.dialogRef.close(true); // Close modal and indicate success
      } else {
        this.errorMessage = 'Unexpected response from server.';
      }
    } catch (error) {
      this.errorMessage = 'Verification failed. Please try again.';
      console.error('OTP verification error:', error);
    } finally {
      this.isLoading = false; // Hide loading spinner
    }
  }

  /**
   * Handles the cancel action.
   * Closes the modal without indicating success.
   */
  onCancel(): void {
    this.dialogRef.close(false); // Close modal and indicate cancellation/failure
  }

  get isOtpIncomplete(): boolean {
    return this.otp.some(char => char === '');
  }
}
